import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const REFRESH_PATH = '/api/v1/auth/refresh';

const AUTH_STORAGE_KEYS = [
  'access_token',
  'refresh_token',
  'super_admin_token',
  'super_admin_refresh_token',
  'tenant_id',
  'user_type',
  'user_role',
  'user_roles',
  'user_id',
  'active_role',
  'user_email',
];

type RetryConfig = InternalAxiosRequestConfig & { _retryAfterRefresh?: boolean };

let pendingRefresh: Promise<string | null> | null = null;

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(key);
}

function getAccessToken(): string | null {
  return readStored('super_admin_token') || readStored('access_token');
}

function getRefreshToken(): string | null {
  return readStored('refresh_token');
}

function isSuperAdmin(): boolean {
  return readStored('user_type') === 'super_admin';
}

function clearAuthStorage(): void {
  if (typeof window === 'undefined') return;
  AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  const onSuperAdminPage = window.location.pathname.startsWith('/super-admin');
  window.location.href = onSuperAdminPage ? '/super-admin/login' : '/login';
}

/**
 * Thrown by refreshAccessToken() when the refresh request itself couldn't be
 * completed (network error, timeout, DNS failure, 5xx — no response from the
 * server at all, or one that doesn't actually speak to whether the refresh
 * token is valid). Distinct from a plain `null` return, which means the
 * server was reached and explicitly rejected the refresh token (401/403) —
 * only that case means the session is genuinely over.
 *
 * The distinction matters most right when a laptop wakes from sleep/lock:
 * that's exactly when the access token is most likely to have expired
 * (real time passed while the tab was suspended) AND when Wi-Fi/VPN is most
 * likely still reconnecting. Without this, that transient blip was
 * indistinguishable from an actually-invalid refresh token, and the caller
 * wiped a perfectly good session over it — logging the user out for no
 * reason other than bad timing on reconnect.
 */
export class RefreshUnavailableError extends Error {}

/**
 * Issue a refresh request. Single-flight: concurrent callers share the same
 * in-flight promise so we don't trigger multiple refreshes when many requests
 * 401 at the same time.
 *
 * Exported for fetchWithAuthRetry (see below) — the raw-fetch call sites
 * that predate the axios client need the exact same refresh-once behavior
 * this interceptor gives axios-based requests.
 *
 * Resolves to the new access token on success, or `null` if the server
 * explicitly rejected the refresh token (genuinely expired/revoked —
 * logout is correct). Throws RefreshUnavailableError if the request itself
 * couldn't be completed (network/transient — logout would be wrong; callers
 * should leave the session alone and let the next attempt retry).
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = (async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return null;

      // Use a bare axios (no interceptors) to avoid recursive refresh on a
      // refresh failure.
      const response = await axios.post(
        `${API_URL}${REFRESH_PATH}`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 },
      );

      const newAccessToken: string | undefined = response.data?.access_token;
      if (!newAccessToken) return null;

      localStorage.setItem('access_token', newAccessToken);
      if (isSuperAdmin()) {
        localStorage.setItem('super_admin_token', newAccessToken);
      }
      return newAccessToken;
    } catch (err) {
      // A response means the server was reached and spoke -- a 401/403 here
      // is a genuine "this refresh token is no longer valid." Anything else
      // (no response at all: network error, timeout, DNS failure) never
      // actually asked the server about the token, so it can't mean the
      // token is invalid.
      if (axios.isAxiosError(err) && err.response) {
        return null;
      }
      throw new RefreshUnavailableError('Could not reach the refresh endpoint');
    } finally {
      // Clear after the next microtask so callers awaiting the same promise
      // resolve, but a subsequent 401 starts a fresh refresh.
      setTimeout(() => {
        pendingRefresh = null;
      }, 0);
    }
  })();

  return pendingRefresh;
}

/**
 * Attach the standard auth headers to outbound requests:
 * - Authorization: Bearer <token>
 * - X-Tenant-ID, X-User-Type, X-User-Roles, X-Active-Role
 *
 * No-op on the server (typeof window === 'undefined').
 */
export function attachAuthRequestInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    if (typeof window === 'undefined') return config;

    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    const tenantId = readStored('tenant_id');
    if (tenantId) config.headers['X-Tenant-ID'] = tenantId;

    const userType = readStored('user_type');
    if (userType) config.headers['X-User-Type'] = userType;

    const roles = readStored('user_roles');
    if (roles) config.headers['X-User-Roles'] = roles;

    const activeRole = readStored('active_role');
    if (activeRole) config.headers['X-Active-Role'] = activeRole;

    return config;
  });
}

/**
 * Attach a response interceptor that refreshes the access token once on 401
 * and retries the original request, redirecting to login on failure.
 *
 * Skips the refresh path itself, the login endpoint, and super-admin tokens
 * (which use a separate refresh flow).
 */
export function attachAuthResponseInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const url = error.config?.url ?? '';

      if (status !== 401) return Promise.reject(error);

      // Don't try to refresh on the login or refresh endpoints themselves.
      if (url.includes('/auth/login') || url.includes(REFRESH_PATH)) {
        return Promise.reject(error);
      }

      const config = error.config as RetryConfig | undefined;
      if (!config || config._retryAfterRefresh) {
        // Already retried once after a refresh, or no config available.
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(error);
      }

      // Super admin tokens do not support the standard refresh path.
      if (isSuperAdmin()) {
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(error);
      }

      let newToken: string | null;
      try {
        newToken = await refreshAccessToken();
      } catch (refreshErr) {
        if (refreshErr instanceof RefreshUnavailableError) {
          // Couldn't even attempt the refresh (network/transient, e.g. the
          // first request right after a laptop wakes from sleep/lock,
          // before Wi-Fi/VPN has reconnected) -- leave the session alone
          // and just surface this one request's failure. The next natural
          // request (or a manual retry) gets another chance to refresh.
          return Promise.reject(error);
        }
        throw refreshErr;
      }
      if (!newToken) {
        clearAuthStorage();
        redirectToLogin();
        return Promise.reject(error);
      }

      config._retryAfterRefresh = true;
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
      return client.request(config as AxiosRequestConfig);
    },
  );
}

/**
 * Convenience: attach both auth interceptors to an axios instance.
 */
export function installAuthInterceptors(client: AxiosInstance): void {
  attachAuthRequestInterceptor(client);
  attachAuthResponseInterceptor(client);
}
